<template>
  <div id="app" class="relative text-cream min-h-screen py-8">
    <div class="max-w-6xl mx-auto px-4">
      <!-- Hero -->
      <div class="relative mb-12 z-10 py-16 rounded-2xl overflow-hidden
           bg-gradient-to-b from-gray-800/70 to-gray-900/70 backdrop-blur-lg
           border-2 border-indigo-400/50
           shadow-[0_0_50px_rgba(99,102,241,0.25)]
           max-w-7xl mx-auto">
        <div class="text-center space-y-12">
          <div class="space-y-4">
            <p class="text-xl font-light text-indigo-200/90">Hey, I'm John</p>
            <p class="text-4xl sm:text-5xl md:text-6xl font-normal leading-normal max-w-4xl mx-auto px-4
                      bg-gradient-to-r from-white via-indigo-100 to-white
                      bg-clip-text text-transparent
                      drop-shadow-[0_0_25px_rgba(255,255,255,0.1)]">
              Building AI Native Web Apps
            </p>
            <p class="text-base sm:text-lg font-light text-indigo-100/90 pt-2">
              <span>20+ years web developer</span>
              <span class="hidden sm:inline text-indigo-300/40 mx-3" aria-hidden="true">·</span>
              <span class="block sm:inline mt-1 sm:mt-0">4+ years building AI-native products</span>
            </p>
          </div>
        </div>
      </div>

      <!-- Intro + CTAs -->
      <section class="relative -mt-4 mb-16">
        <div class="max-w-5xl mx-auto">
          <div class="text-center mb-8 px-6 py-8">
            <p class="text-lg font-extralight text-cream/80 leading-relaxed max-w-3xl mx-auto">
              If your team needs someone who can understand the problem and build it, we can start with a call.
            </p>
            <p class="text-base font-light text-indigo-200/90 mt-8">
              Contract work from $75/hour. I'll tell you what the first stretch will cost before it starts.
            </p>
            <div class="mt-6">
              <ContactActions
                align="center"
                :form-open="formOpen"
                :contact-form="contactForm"
                :status="status"
                @toggle="$emit('toggle')"
                @submit="$emit('submit')"
                @update:contact-form="$emit('update:contactForm', $event)"
              />
            </div>
          </div>
        </div>
      </section>

      <!-- Projects strip -->
      <section class="mb-20">
        <div class="max-w-5xl mx-auto">
          <div class="flex items-center gap-4 mb-4">
            <div class="h-px flex-1 bg-indigo-500/30"></div>
            <span class="text-sm uppercase tracking-widest text-indigo-200/80">Recent Builds</span>
            <div class="h-px flex-1 bg-indigo-500/30"></div>
          </div>
          <p class="text-center text-sm font-extralight text-cream/60 mb-8">
            Click a project to expand it.
          </p>

          <div class="space-y-4">
            <div
              v-for="project in projects"
              :key="project.id"
              :ref="(el) => setCardRef(project.id, el)"
              class="rounded-xl
                     bg-gradient-to-b from-gray-800/40 via-indigo-900/15 to-gray-900/40
                     backdrop-blur-sm border transition-all duration-300 scroll-mt-8"
              :class="[
                projectColors[project.id].border,
                projectColors[project.id].glow,
                selectedProjectId === project.id
                  ? 'from-gray-800/60 via-indigo-900/25 to-gray-900/60 border-opacity-80'
                  : 'hover:from-gray-800/50 hover:via-indigo-900/20 hover:to-gray-900/50 border-opacity-40'
              ]"
            >
              <button
                type="button"
                @click="toggleProject(project.id)"
                class="w-full text-left p-6 group"
                :aria-expanded="selectedProjectId === project.id ? 'true' : 'false'"
              >
                <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
                  <div class="flex items-center gap-3 min-w-0">
                    <span
                      class="h-2.5 w-2.5 rounded-full shrink-0"
                      :class="projectColors[project.id].dot"
                    ></span>
                    <h3 class="text-xl font-light text-cream truncate">{{ project.name }}</h3>
                    <span class="text-sm font-extralight text-cream/50 hidden sm:inline">
                      {{ project.title }}
                    </span>
                  </div>
                  <div class="flex items-center gap-3 shrink-0 sm:flex-col sm:items-end">
                    <span class="text-xs font-extralight text-cream/50 uppercase tracking-wider">
                      {{ project.ownership }}
                    </span>
                    <span class="inline-flex items-center gap-1 text-xs font-extralight text-indigo-200/90 group-hover:text-indigo-100">
                      {{ selectedProjectId === project.id ? 'Hide details' : 'View details' }}
                      <svg
                        class="w-3.5 h-3.5 transition-transform duration-300"
                        :class="selectedProjectId === project.id ? 'rotate-180' : ''"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
                      </svg>
                    </span>
                  </div>
                </div>

                <p class="text-base font-extralight leading-relaxed text-cream/85 mb-4 sm:pl-5">
                  {{ project.summary }}
                </p>

                <div class="flex flex-wrap gap-2 sm:pl-5">
                  <span
                    v-for="capId in project.capabilityIds"
                    :key="capId"
                    class="px-2.5 py-1 rounded-full text-xs font-extralight border"
                    :class="projectColors[project.id].badge"
                  >
                    {{ capabilityLabel(capId) }}
                  </span>
                </div>
              </button>

              <div
                v-if="selectedProjectId === project.id"
                class="px-6 pb-6 sm:pl-11 border-t border-white/10 pt-5"
              >
                <ul class="space-y-3 mb-6">
                  <li
                    v-for="(bullet, index) in project.bullets"
                    :key="index"
                    class="flex gap-3 text-base font-extralight leading-relaxed text-cream/90"
                  >
                    <span
                      class="mt-2 h-1.5 w-1.5 rounded-full shrink-0"
                      :class="projectColors[project.id].dot"
                    ></span>
                    <span>{{ bullet }}</span>
                  </li>
                </ul>

                <div class="flex flex-wrap gap-2">
                  <span
                    v-for="tech in project.tech"
                    :key="tech"
                    class="px-3 py-1 bg-indigo-800/40 border border-indigo-500/40
                           rounded-full text-xs font-extralight text-indigo-100"
                  >
                    {{ tech }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Capabilities -->
      <section class="mb-20">
        <div class="max-w-5xl mx-auto">
          <div class="flex items-center gap-4 mb-4">
            <div class="h-px flex-1 bg-indigo-500/30"></div>
            <span class="text-sm uppercase tracking-widest text-indigo-200/80">How I Work</span>
            <div class="h-px flex-1 bg-indigo-500/30"></div>
          </div>
          <p class="text-center text-base font-extralight text-cream/70 mb-10 max-w-2xl mx-auto">
            Patterns that show up across products — click a project badge to jump to that build.
          </p>

          <div class="space-y-6">
            <div
              v-for="capability in capabilities"
              :key="capability.id"
              class="rounded-xl p-6
                     bg-gradient-to-b from-gray-800/30 via-indigo-900/10 to-gray-900/30
                     backdrop-blur-sm border border-white/10
                     hover:border-white/20 transition-all duration-300"
            >
              <h3 class="text-xl font-light text-cream mb-2">{{ capability.title }}</h3>
              <p class="text-base font-extralight leading-relaxed text-cream/80 mb-4">
                {{ capability.summary }}
              </p>
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="projectId in capability.projectIds"
                  :key="projectId"
                  type="button"
                  @click="selectProject(projectId)"
                  class="px-3 py-1 rounded-full text-xs font-extralight border
                         transition-all duration-200 hover:scale-[1.03]"
                  :class="[
                    projectColors[projectId].badge,
                    selectedProjectId === projectId ? 'ring-1 ring-white/30' : ''
                  ]"
                >
                  {{ projectColors[projectId].label }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Footer links -->
      <div class="text-center pb-12">
        <p class="text-base font-extralight text-cream/70">
          I also offer traditional web development services —
          <button
            type="button"
            @click="$emit('go-web')"
            class="text-indigo-400 hover:text-indigo-300 underline underline-offset-2 transition-colors"
          >
            view services
          </button>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import ContactActions from './ContactActions.vue';
import { projects, capabilities, projectColors } from '../data/aiV2';

export default {
  name: 'AiV2Page',
  components: {
    ContactActions
  },
  props: {
    formOpen: {
      type: Boolean,
      default: false
    },
    contactForm: {
      type: Object,
      required: true
    },
    status: {
      type: String,
      default: null
    }
  },
  emits: ['toggle', 'submit', 'update:contactForm', 'go-web'],
  data() {
    return {
      projects,
      capabilities,
      projectColors,
      selectedProjectId: null
    };
  },
  created() {
    this.cardRefs = {};
  },
  methods: {
    setCardRef(id, el) {
      if (el) this.cardRefs[id] = el;
    },
    capabilityLabel(capId) {
      const cap = this.capabilities.find((c) => c.id === capId);
      return cap ? cap.shortLabel || cap.title : capId;
    },
    toggleProject(projectId) {
      this.selectedProjectId = this.selectedProjectId === projectId ? null : projectId;
    },
    selectProject(projectId) {
      this.selectedProjectId = projectId;
      this.$nextTick(() => {
        const card = this.cardRefs[projectId];
        if (card) {
          card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      });
    }
  }
};
</script>
